import { yupResolver } from '@hookform/resolvers/yup'
import CatchingPokemonIcon from '@mui/icons-material/CatchingPokemon'
import EventIcon from '@mui/icons-material/Event'
import MapIcon from '@mui/icons-material/Map'
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox'
import SummarizeIcon from '@mui/icons-material/Summarize'
import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { getSurveys, UIAction } from 'app/slices/UI'
import DialogConfirm from 'components/Dialogs/DialogConfirm'
import RenderSurvey from 'components/Event/RenderSurvey'
import { format } from 'date-fns'
import postal_code from 'japan-postal-code'
import { useSnackbar } from 'notistack'
import { Fragment, useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'
import { loadImageProcessToCanvas } from 'utils/imageConvert'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import * as yup from 'yup'

interface FormInput {
  prefectureId: number
  name: string
  address: string
  startDate: number
  endDate: number
  summary: string
  useGPS: boolean
  rewardSummary: string
}

export default function EventRegister() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const schema = yup.object({
    prefectureId: yup.number().required(),
    name: yup.string().required(),
    address: yup.string().required(),
    summary: yup.string().required(),
  })
  const UIPrefectures = useAppSelector((s) => s.UI.prefectures)
  const surveys = useAppSelector((s) => s.UI.surveys).filter((f) => f.eventId === null)
  const auth = useAppSelector((s) => s.UI.auth)
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      useGPS: true,
      startDate: new Date().setHours(0,0,0),
      endDate: new Date().setHours(23,59,59),
      prefectureId: 0,
    },
  })
  const inputRef = useRef({} as HTMLInputElement)
  const inputRewardRef = useRef({} as HTMLInputElement)
  const inputFloorRef = useRef({} as HTMLInputElement)
  const [surveyIds, setSurveyIds] = useState<number[]>([])
  const [image, setImage] = useState<HTMLCanvasElement>()
  const [rewardImage, setRewardImage] = useState<HTMLCanvasElement>()
  const [floors, setFloors] = useState<
    {
      name: string
      image?: HTMLCanvasElement
    }[]
  >([])
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<FormInput>()
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (auth) {
      dispatch(getSurveys())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth])
  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: 'イベント一覧',
          url: '/event',
        },
        {
          text: 'イベント登録',
          url: '/event/register',
        },
      ])
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (!auth) return <></>

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (!image) {
      const msg = 'イベント画像が必要です'
      enqueueSnackbar(msg, {
        variant: 'error',
      })
      document.getElementById('upload-image-btn')?.scrollIntoView()
      return
    }
    setData(data)
  }
  const handleCreate = async () => {
    try {
      setLoading(true)
      if (!image) throw new Error('image required')
      let formData = new FormData()
      await new Promise((rs) => {
        image.toBlob((blob) => {
          if (blob) {
            const ext = blob.type.split('/').pop()
            let file = new File(
              [blob],
              'image' +
                auth.client +
                '-' +
                auth.id +
                '-' +
                new Date().getTime() +
                '.' +
                ext,
              {
                type: blob.type,
              }
            )
            formData.append('image', file)
            rs(1)
          }
        })
      })
      for (const { image, name } of floors) {
        if (!image || !name) continue
        await new Promise((rs) => {
          image.toBlob((blob) => {
            if (blob) {
              const ext = blob.type.split('/').pop()
              let file = new File(
                [blob],
                'floorImage' +
                  auth.client +
                  '-' +
                  auth.id +
                  '-' +
                  new Date().getTime() +
                  '.' +
                  ext,
                {
                  type: blob.type,
                }
              )
              formData.append('floorImage', file)
              rs(1)
            }
          })
        })
      }
      if (rewardImage)
        await new Promise((rs) => {
          rewardImage.toBlob((blob) => {
            if (blob) {
              const ext = blob.type.split('/').pop()
              let file = new File(
                [blob],
                'rewardImage' +
                  auth.client +
                  '-' +
                  auth.id +
                  '-' +
                  new Date().getTime() +
                  '.' +
                  ext,
                {
                  type: blob.type,
                }
              )
              formData.append('rewardImage', file)
              rs(1)
            }
          })
        })
      formData.append(
        'data',
        JSON.stringify({
          ...data,
          surveyIds,
          floors: floors.map((v, i) => ({ name: v.name, order: i + 1 })),
        })
      )
      const res = await axiosClient.post('/api/events/register', formData)
      setLoading(false)
      navigate('/event/' + res.data.id)
    } catch (_error) {
      const msg = 'エラーが発生しました。'
      enqueueSnackbar(msg, {
        variant: 'error',
      })
    }
  }
  return (
    <>
      {!!data && (
        <DialogConfirm
          maxWidth="md"
          loading={loading}
          handleSubmit={handleCreate}
          title="登録内容の確認"
          primaryButtonText="登録"
          open={true}
          handleClose={() => setData(undefined)}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              イベント名
            </Typography>
            <Typography
              variant="body1"
              component="h4"
              sx={{
                flex: 1,
              }}
              className="mb-4 md:mb-0"
            >
              {data.name}
            </Typography>
          </Stack>
          <Typography
            variant="body1"
            component="h4"
            sx={{
              width: 130,
              fontWeight: 'bold',
            }}
          >
            イベントイメージ
          </Typography>
          <img
            width={image?.width}
            src={image?.toDataURL()}
            alt="画像"
            style={{
              maxWidth: '100%',
              border: '1px solid #999',
            }}
          ></img>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              開催場所
            </Typography>
            <Typography>
              {UIPrefectures.find((f) => f.id === data.prefectureId)?.name}
              {data.address}
            </Typography>
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              開催期間
            </Typography>
            <Typography>
              {format(data.startDate, 'yyyy/MM/dd')} ～{' '}
              {format(data.endDate, 'yyyy/MM/dd')}
            </Typography>
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              概要説明文
            </Typography>
            <Typography>{data.summary}</Typography>
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              GPSの使用
            </Typography>
            <Typography
              fontWeight="bold"
              variant="body1"
              sx={{
                color: (theme) =>
                  data.useGPS
                    ? theme.palette.success.main
                    : theme.palette.warning.dark,
              }}
            >
              {data.useGPS ? '有効' : '無効'}
            </Typography>
          </Stack>
          {getValues('useGPS') && (
            <>
              {floors.map((v, i) => {
                return (
                  <Fragment key={i}>
                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                    >
                      <Typography
                        variant="body1"
                        component="h4"
                        sx={{
                          width: 130,
                          fontWeight: 'bold',
                        }}
                      >
                        フロア名
                      </Typography>
                      <Typography
                        variant="body1"
                        component="h4"
                        sx={{
                          flex: 1,
                        }}
                        className="mb-4 md:mb-0"
                      >
                        {v.name}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      component="h4"
                      sx={{
                        width: 130,
                        fontWeight: 'bold',
                      }}
                    >
                      フロアイメージ
                    </Typography>
                    <img
                      width={v.image?.width}
                      src={v.image?.toDataURL()}
                      alt="フロア画像"
                      style={{
                        maxWidth: '100%',
                        border: '1px solid #999',
                      }}
                    ></img>
                  </Fragment>
                )
              })}
            </>
          )}
          {data.rewardSummary && (
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
            >
              <Typography
                variant="body1"
                component="h4"
                sx={{
                  width: 130,
                  fontWeight: 'bold',
                }}
              >
                特典説明文
              </Typography>
              <Typography
                variant="body1"
                component="h4"
                sx={{
                  flex: 1,
                }}
                className="mb-4 md:mb-0"
              >
                {data.rewardSummary}
              </Typography>
            </Stack>
          )}
          {rewardImage && (
            <>
              <Typography
                variant="body1"
                component="h4"
                sx={{
                  width: 130,
                  fontWeight: 'bold',
                }}
              >
                特典イメージ
              </Typography>
              <img
                width={rewardImage?.width}
                src={rewardImage?.toDataURL()}
                alt="画像"
                style={{
                  maxWidth: '100%',
                  border: '1px solid #999',
                }}
              ></img>
            </>
          )}
          {surveyIds.length > 0 && (
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              アンケート項目選択
            </Typography>
          )}
          {surveyIds.sort().map((id) => {
            const surveyText = surveys.find((f) => f.id === id)?.text || ''
            return (
              <Typography
                key={id}
                variant="body1"
                component="h4"
                sx={{
                  flex: 1,
                }}
                className="mb-4 md:mb-0"
              >
                ・{surveyText}
              </Typography>
            )
          })}
        </DialogConfirm>
      )}
      <Grid container spacing={1} mt={2}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            component="h2"
            sx={{ borderBottom: 2 }}
            fontWeight="bold"
          >
            イベント作成
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box
            maxWidth="md"
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography variant="h6" component="h3" fontWeight="bold">
              イベント情報入力
            </Typography>
            <Typography variant="body1" component="p" fontWeight="bold">
              基本情報
            </Typography>
            <TextField
              required
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EventIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              label="イベント名"
              fullWidth
              variant="standard"
              {...register('name')}
              error={'name' in errors}
            />
            {image && (
              <Stack my={2}>
                <img
                  src={image.toDataURL()}
                  alt="画像"
                  style={{
                    border: '1px solid #999',
                    width: image.width,
                    maxWidth: '100%',
                  }}
                ></img>
              </Stack>
            )}
            <Stack my={2} direction="row" spacing={2}>
              <input
                ref={inputRef}
                accept="image/*"
                style={{ display: 'none' }}
                id="upload-file"
                type="file"
                onChange={async (e) => {
                  if (e.target.files) {
                    setImage(await loadImageProcessToCanvas(e.target.files[0]))
                    inputRef.current.value = ''
                  }
                }}
              />

              {image ? (
                <Button
                  variant="contained"
                  component="span"
                  size="small"
                  color="warning"
                  onClick={() => {
                    inputRef.current.value = ''
                    setImage(undefined)
                  }}
                  style={{ width: 'max-content', minWidth: 200, marginLeft: 0 }}
                >
                  画像を削除
                </Button>
              ) : (
                <label
                  htmlFor="upload-file"
                  style={{ width: 'max-content', marginLeft: 0 }}
                >
                  <Button
                    id="upload-image-btn"
                    variant="contained"
                    component="span"
                    size="small"
                    color="secondary"
                    sx={{
                      minWidth: 200,
                    }}
                  >
                    イベント画像のアップロード
                  </Button>
                </label>
              )}
            </Stack>
            <TextField
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MarkunreadMailboxIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              label="郵便番号"
              fullWidth
              onChange={(e) => {
                const value = e.target.value
                if (value.length === 7) {
                  postal_code.get(value, (address) => {
                    const { area, city, prefecture, street } = address
                    const pre = UIPrefectures.find((f) => f.name === prefecture)
                    if (pre) {
                      setValue('prefectureId', pre.id, {
                        shouldValidate: true,
                      })
                    }
                    setValue('address', city + area + street, {
                      shouldValidate: true,
                    })
                  })
                }
              }}
              variant="standard"
            />
            <Autocomplete
              disablePortal
              options={[0, ...UIPrefectures.map((v) => v.id)]}
              getOptionLabel={(id) =>
                UIPrefectures.find((f) => f.id === id)?.name || ''
              }
              fullWidth
              value={getValues('prefectureId')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={'都道府県'}
                  required
                  variant="standard"
                  error={'prefectureId' in errors}
                />
              )}
              onChange={(_event, newValue) => {
                newValue &&
                  setValue('prefectureId', newValue, {
                    shouldValidate: true,
                  })
              }}
            />
            <TextField
              required
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MapIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              label="開催場所"
              fullWidth
              variant="standard"
              {...register('address')}
              error={'address' in errors}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent={{ xs: 'flex-start', sm: 'flex-start' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              my={2}
            >
              <Typography variant="body2" component="p" sx={{ width: 100 }}>
                開催期間
              </Typography>
              <DatePicker
                label="start"
                disablePast
                inputFormat="yyyy/MM/dd"
                value={new Date(getValues('startDate'))}
                onChange={(date, keyboardInputValue) => {
                  if (
                    !keyboardInputValue &&
                    date &&
                    date instanceof Date &&
                    !isNaN(date.valueOf())
                  ) {
                    setValue('startDate', date.setHours(0,0,0), {
                      shouldValidate: true,
                    })
                    if (date.getTime() > getValues('endDate')) {
                      setValue('endDate', date.setHours(23,59,59), {
                        shouldValidate: true,
                      })
                    }
                  }
                }}
                renderInput={(params) => <TextField {...params} />}
              />
              <Typography
                variant="body2"
                component="p"
                sx={{ width: 60 }}
                textAlign="center"
              >
                から
              </Typography>
              <DatePicker
                label="end"
                disablePast
                minDate={new Date(getValues('startDate'))}
                inputFormat="yyyy/MM/dd"
                value={new Date(getValues('endDate'))}
                onChange={(date, keyboardInputValue) => {
                  if (
                    !keyboardInputValue &&
                    date &&
                    date instanceof Date &&
                    !isNaN(date.valueOf())
                  ) {
                    setValue('endDate', date.setHours(23,59,59), {
                      shouldValidate: true,
                    })
                  }
                }}
                renderInput={(params) => <TextField {...params} />}
              />
            </Stack>
            <TextField
              required
              multiline
              rows={5}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SummarizeIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              label="概要説明文"
              fullWidth
              variant="outlined"
              {...register('summary')}
              error={'summary' in errors}
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  checked={getValues('useGPS')}
                  onChange={(e) => {
                    setValue('useGPS', e.target.checked, {
                      shouldValidate: true,
                    })
                    if (floors.length === 0) {
                      setFloors([
                        {
                          name: '',
                        },
                      ])
                    }
                    if (e.target.checked) {
                      setFloors([])
                    }
                  }}
                />
              }
              sx={{
                color: 'red',
                userSelect: 'none',
              }}
              label="GPS（位置情報）の使用"
            />
            {!getValues('useGPS') && (
              <>
                <Typography variant="body1" component="p" fontWeight="bold">
                  ガイドマップのアップロード
                </Typography>
                {floors.map((v, i) => {
                  return (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                      key={i}
                    >
                      <Stack flex={1}>
                        <TextField
                          required
                          margin="dense"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EventIcon color="primary" />
                              </InputAdornment>
                            ),
                          }}
                          label="フロア名"
                          fullWidth
                          variant="standard"
                          value={v.name}
                          onChange={(e) => {
                            const tmp = [...floors]
                            tmp.splice(i, 1, {
                              ...tmp[i],
                              name: e.target.value,
                            })
                            setFloors(tmp)
                          }}
                        />
                        {v.image && (
                          <Stack my={2}>
                            <img
                              src={v.image.toDataURL()}
                              alt="画像"
                              style={{
                                border: '1px solid #999',
                                width: v.image.width,
                                maxWidth: '100%',
                              }}
                            ></img>
                          </Stack>
                        )}
                        <Stack my={2} direction="row" spacing={2}>
                          <input
                            ref={inputFloorRef}
                            accept="image/*"
                            style={{ display: 'none' }}
                            id={'upload-floor' + i}
                            type="file"
                            onChange={async (e) => {
                              if (e.target.files) {
                                const tmp = [...floors]
                                tmp.splice(i, 1, {
                                  ...tmp[i],
                                  image: await loadImageProcessToCanvas(
                                    e.target.files[0]
                                  ),
                                })
                                setFloors(tmp)
                                inputFloorRef.current.value = ''
                              }
                            }}
                          />

                          <label
                            htmlFor={'upload-floor' + i}
                            style={{ width: 'max-content', marginLeft: 0 }}
                          >
                            <Button
                              variant="contained"
                              component="span"
                              size="small"
                              color="secondary"
                              sx={{
                                minWidth: 150,
                              }}
                            >
                              {v.image ? '再アップロード' : 'アップロード'}
                            </Button>
                          </label>
                        </Stack>
                      </Stack>
                      <Stack
                        width={'3rem'}
                        direction="row"
                        justifyContent="center"
                      >
                        <div>
                          {floors.length !== 1 && (
                            <IconButton
                              aria-label="delete"
                              color="error"
                              onClick={() => {
                                const tmp = [...floors]
                                tmp.splice(i, 1)
                                setFloors(tmp)
                              }}
                            >
                              <RemoveCircleIcon />
                            </IconButton>
                          )}
                        </div>
                      </Stack>
                    </Stack>
                  )
                })}
                <Stack direction="row" justifyContent="center">
                  <div>
                    <IconButton
                      aria-label="add"
                      color="primary"
                      size="large"
                      onClick={() => setFloors([...floors, { name: '' }])}
                    >
                      <AddCircleIcon fontSize="large" />
                    </IconButton>
                  </div>
                </Stack>
              </>
            )}
            <Typography variant="body1" component="p" fontWeight="bold">
              特典情報
            </Typography>
            <TextField
              multiline
              rows={5}
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SummarizeIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              label="説明文"
              fullWidth
              variant="outlined"
              {...register('rewardSummary')}
              error={'rewardSummary' in errors}
            />
            {rewardImage && (
              <Stack my={2}>
                <img
                  src={rewardImage.toDataURL()}
                  alt="画像"
                  style={{
                    border: '1px solid #999',
                    width: rewardImage.width,
                    maxWidth: '100%',
                  }}
                ></img>
              </Stack>
            )}
            <Stack my={2} direction="row" spacing={2}>
              <input
                ref={inputRewardRef}
                accept="image/*"
                style={{ display: 'none' }}
                id="upload-reward"
                type="file"
                onChange={async (e) => {
                  if (e.target.files) {
                    setRewardImage(
                      await loadImageProcessToCanvas(e.target.files[0])
                    )
                    inputRewardRef.current.value = ''
                  }
                }}
              />

              {rewardImage ? (
                <Button
                  variant="contained"
                  component="span"
                  size="small"
                  color="warning"
                  onClick={() => {
                    inputRewardRef.current.value = ''
                    setRewardImage(undefined)
                  }}
                  style={{ width: 'max-content', minWidth: 200, marginLeft: 0 }}
                >
                  特典画像を削除
                </Button>
              ) : (
                <label
                  htmlFor="upload-reward"
                  style={{ width: 'max-content', marginLeft: 0 }}
                >
                  <Button
                    variant="contained"
                    component="span"
                    size="small"
                    color="secondary"
                    sx={{
                      minWidth: 200,
                    }}
                  >
                    特典画像のアップロード
                  </Button>
                </label>
              )}
            </Stack>
            <Typography variant="body1" component="p" fontWeight="bold">
              アンケートの利用（イベント作成後、下記以外の項目を作成可能）
            </Typography>
            {surveys.map((v, i) => {
              return (
                <Stack key={i}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={surveyIds.includes(v.id)}
                        onChange={(e) => {
                          const index = surveyIds.findIndex((f) => f === v.id)
                          if (index < 0 && e.target.checked) {
                            setSurveyIds([...surveyIds, v.id])
                          } else if (index >= 0 && !e.target.checked) {
                            setSurveyIds(surveyIds.filter((f) => f !== v.id))
                          }
                        }}
                      />
                    }
                    label={v.text}
                  />
                  <Box mx={2} p={1} bgcolor="#eee">
                    <Typography>例</Typography>
                    <RenderSurvey survey={v} />
                  </Box>
                </Stack>
              )
            })}
            <Stack justifyContent="center" direction="row">
              <LoadingButton
                disabled={Object.keys(errors).length > 0 || !image}
                variant="contained"
                type="submit"
                sx={{
                  mt: 4,
                  px: 4,
                }}
                color={'primary'}
                loadingPosition="start"
                startIcon={<CatchingPokemonIcon />}
              >
                イベント作成
              </LoadingButton>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}
